import { exact } from "prop-types";
import { PATH_APP } from "../paths";
import React, { lazy } from "react";
import { Redirect } from "react-router-dom";
import AuthProtect from "src/components/Auth/AuthProtect";
import DashboardLayout from "src/layouts/DashboardLayout";
import Roles from "src/utils/Roles";
import { legacyLiveDebugTarget, legacyVersionsTarget } from "src/views/app/Admin/control/controlLinks";

// ----------------------------------------------------------------------

const AdminRoutes = {
  path: PATH_APP.admin.root,
  guard: AuthProtect,
  layout: DashboardLayout,
  routes: [
    // tucarta
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN, Roles.USER_DISTRIBUTOR],
      path: PATH_APP.admin.licences,
      component: lazy(() => import("src/views/app/Admin/licences/Index")),
    },
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN, Roles.USER_DISTRIBUTOR],
      path: PATH_APP.admin.root,
      component: () => <Redirect to={PATH_APP.admin.licences} />,
    },
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN, Roles.USER_DISTRIBUTOR],
      path: PATH_APP.admin.addLicence,
      component: lazy(
        () => import("src/views/app/Admin/licences/LicencesAddEdit"),
      ),
    },
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN, Roles.USER_DISTRIBUTOR],
      path: PATH_APP.admin.editLicence,
      component: lazy(
        () => import("src/views/app/Admin/licences/LicencesAddEdit"),
      ),
    },
    {
      // Facturación de la licencia (Stripe vs BD, acciones, reembolsos):
      // solo admin de plataforma.
      exact: true,
      role: [Roles.GLOBAL_ADMIN],
      path: PATH_APP.admin.licenceBilling,
      component: lazy(() => import("src/views/app/Admin/licenceBilling")),
    },
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN, Roles.USER_DISTRIBUTOR],
      path: PATH_APP.admin.invoices,
      component: lazy(() => import("src/views/app/Admin/invoices/Index")),
    },
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN, Roles.USER_DISTRIBUTOR],
      path: PATH_APP.admin.addInvoice,
      component: lazy(
        () => import("src/views/app/Admin/invoices/InvoicesAddEdit"),
      ),
    },
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN, Roles.USER_DISTRIBUTOR],
      path: PATH_APP.admin.editInvoice,
      component: lazy(
        () => import("src/views/app/Admin/invoices/InvoicesAddEdit"),
      ),
    },
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN],
      path: PATH_APP.admin.plans,
      component: lazy(() => import("src/views/app/Admin/plans/Index")),
    },
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN],
      path: PATH_APP.admin.addPlan,
      component: lazy(() => import("src/views/app/Admin/plans/PlansAddEdit")),
    },
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN],
      path: PATH_APP.admin.editPlan,
      component: lazy(() => import("src/views/app/Admin/plans/PlansAddEdit")),
    },
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN],
      path: PATH_APP.admin.addOns,
      component: lazy(() => import("src/views/app/Admin/addOns/AddOnsList")),
    },
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN],
      path: PATH_APP.admin.distributors,
      component: lazy(() => import("src/views/app/Admin/distributors/index")),
    },
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN],
      path: PATH_APP.admin.addDistributor,
      component: lazy(
        () => import("src/views/app/Admin/distributors/DistributorsAddEdit"),
      ),
    },
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN],
      path: PATH_APP.admin.editDistributor,
      component: lazy(
        () => import("src/views/app/Admin/distributors/DistributorsAddEdit"),
      ),
    },
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN],
      path: PATH_APP.admin.notifications,
      component: lazy(
        () => import("src/views/app/Admin/notifications/NotificationList"),
      ),
    },
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN],
      path: PATH_APP.admin.addNotification,
      component: lazy(
        () => import("src/views/app/Admin/notifications/NotificationCreate"),
      ),
    },
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN],
      path: PATH_APP.admin.editNotification,
      component: lazy(
        () => import("src/views/app/Admin/notifications/NotificationCreate"),
      ),
    },
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN],
      path: PATH_APP.admin.observability,
      component: lazy(() => import("src/views/app/Admin/observability")),
    },
    {
      // 2026-10-26 · Centro de control (solo administrador global): estado,
      // versiones y workers de todos los restaurantes.
      exact: true,
      role: [Roles.GLOBAL_ADMIN],
      path: PATH_APP.admin.control,
      component: lazy(() => import("src/views/app/Admin/control")),
    },
    {
      // Ficha de un restaurante en el Centro de control.
      exact: true,
      role: [Roles.GLOBAL_ADMIN],
      path: PATH_APP.admin.controlRestaurant,
      component: lazy(() => import("src/views/app/Admin/control/RestaurantControl")),
    },
    {
      // URL antigua: /admin/live-debug[?rid&name&action&at] → Centro de control.
      exact: true,
      role: [Roles.GLOBAL_ADMIN],
      path: PATH_APP.admin.liveDebug,
      component: ({ location }) => <Redirect to={legacyLiveDebugTarget(location?.search)} />,
    },
    {
      // URL antigua: /admin/versions → Centro de control › Versiones.
      exact: true,
      role: [Roles.GLOBAL_ADMIN],
      path: PATH_APP.admin.versions,
      component: ({ location }) => <Redirect to={legacyVersionsTarget(location?.search)} />,
    },
    {
      exact: true,
      role: [Roles.GLOBAL_ADMIN],
      path: PATH_APP.admin.social,
      component: lazy(() => import("src/views/app/Admin/social")),
    },
    {
      component: () => <Redirect to="/404" />,
    },
  ],
};

export default AdminRoutes;
